import { Controller, useForm, useWatch } from 'react-hook-form';
import { useCountryConfig } from 'hooks/useCountryConfig';
import { Column, Row } from 'Components/Grid';
import { editCompanyProfile, getCompanyBySlug } from 'services/company-service';
import { Container } from 'Components/Container';
import { Card } from 'Components/Card';
import { ButtonForm, CustomTextField } from 'Components/Form/styles';
import { FormControl, FormHelperText, TextField, Button, Avatar, Box, Typography, Select, MenuItem, InputLabel } from '@mui/material';
import { useQuery } from 'react-query';
import { Shimmer } from 'Components/PageLoader/styles';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import { useSelector, useDispatch } from 'react-redux';
import { useAuthTenant, updateTenant } from 'store/auth';
import { MuiChipsInput } from 'mui-chips-input';
import { PageHelper } from '../../Components/PageHelper';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * LAYER 1 OF PROTECTION - Frontend Whitelist
 *
 * Only these fields can be edited via the profile page
 * Protected fields (CNPJ, signedPlan, roles, managers, etc.) are NOT sent to backend
 */
const EDITABLE_FIELDS = [
  'name',
  'corporateName',
  'description',
  'size',
  'income',
  'industry',
  'domains',
  'logo',
  'defaultLanguage',
  'timezone',
];

const LATAM_TIMEZONES = [
  { value: 'America/Sao_Paulo',             label: 'Brasília (UTC-3)' },
  { value: 'America/Manaus',                label: 'Manaus (UTC-4)' },
  { value: 'America/Fortaleza',             label: 'Fortaleza (UTC-3)' },
  { value: 'America/Belem',                 label: 'Belém (UTC-3)' },
  { value: 'America/Santiago',              label: 'Santiago, Chile (UTC-3/-4)' },
  { value: 'America/Bogota',                label: 'Bogotá, Colômbia (UTC-5)' },
  { value: 'America/Lima',                  label: 'Lima, Peru (UTC-5)' },
  { value: 'America/Argentina/Buenos_Aires',label: 'Buenos Aires, Argentina (UTC-3)' },
  { value: 'America/Guayaquil',             label: 'Quito/Guayaquil, Equador (UTC-5)' },
  { value: 'America/Montevideo',            label: 'Montevidéu, Uruguai (UTC-3)' },
  { value: 'America/Caracas',               label: 'Caracas, Venezuela (UTC-4)' },
  { value: 'America/La_Paz',                label: 'La Paz, Bolívia (UTC-4)' },
  { value: 'America/Asuncion',              label: 'Assunção, Paraguai (UTC-4/-3)' },
  { value: 'America/Mexico_City',           label: 'Cidade do México (UTC-6)' },
  { value: 'UTC',                           label: 'UTC (Universal)' },
];

export const CompanyProfile = () => {
  const { t } = useTranslation(['company']);
  const dispatch = useDispatch();
  const SwalReact = withReactContent(Swal);
  const selectedTenant = useSelector(useAuthTenant);
  const [logoPreview, setLogoPreview] = useState('');

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    getValues,
    setValue,
  } = useForm({
    defaultValues: {
      active: true,
      country: '',
      cnpj: '',
      corporateName: '',
      description: '',
      domains: [],
      income: '',
      industry: '',
      managers: [],
      name: '',
      partner: false,
      roles: [],
      size: '',
      slug: '',
      logo: '',
      defaultLanguage: '',
      timezone: '',
    },
  });

  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        SwalReact.fire({
          title: t('company:companyProfile.errorTitle'),
          text: t('company:companyProfile.logo.sizeError'),
          icon: 'error',
        });
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        setValue('logo', base64String);
        setLogoPreview(base64String);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = () => {
    setValue('logo', '');
    setLogoPreview('');
  };

  /**
   * LAYER 1A: Whitelist - Only send allowed fields
   * Filters the form values to include ONLY editable fields
   */
  const formmatValues = values => {
    const filtered = {};
    EDITABLE_FIELDS.forEach(field => {
      if (values[field] !== undefined) {
        filtered[field] = values[field] ?? '';
      }
    });
    return filtered;
  };

  /**
   * LAYER 1: Frontend Protection
   * Uses editCompanyProfile endpoint instead of editCompany
   * Only sends whitelisted fields to the backend
   */
  const handleComplete = async () => {
    try {
      const formValues = formmatValues(getValues());

      const res = await editCompanyProfile(selectedTenant._id, formValues);

      if (res.status === 200) {
        // Atualizar o Redux state com as novas informações da empresa
        const updatedTenant = {
          ...selectedTenant,
          ...formValues,
        };
        dispatch(updateTenant({ tenant: updatedTenant }));

        // Atualizar o preview local também
        if (formValues.logo) {
          setLogoPreview(formValues.logo);
        }

        SwalReact.fire({
          title: t('company:companyProfile.successTitle'),
          text: t('company:companyProfile.successMessage'),
          icon: 'success',
        });
      } else {
        throw new Error('Ocorreu um erro ao tentar editar a Empresa!');
      }
    } catch (error) {
      console.error('Erro ao salvar:', error);
      SwalReact.fire({
        title: t('company:companyProfile.errorTitle'),
        text: error.response?.data?.message ?? error.message,
        icon: 'error',
      });
    }
  };
  const countryValue = useWatch({ control, name: 'country' });
  const { allConfigs } = useCountryConfig(countryValue);
  const countryLabel = allConfigs.find(c => c.countryCode === countryValue)?.name?.pt || countryValue || '';

  const { isLoading, isSuccess, isError } = useQuery(['getCompanyBySlug'], async () => {
    if (selectedTenant.slug === undefined) return null;
    const result = await getCompanyBySlug(selectedTenant.slug);

    if (result?.data) {
      Object.keys(result?.data).forEach(key => {
        setValue(key, result?.data[key]);
      });
      if (result?.data?.logo) {
        setLogoPreview(result.data.logo);
      }
    }
    return result?.data;
  });

  // Configuração dos passos do tour interativo
  const tourSteps = [
    {
      target: 'body',
      content: 'Bem-vindo ao Perfil da Empresa! Aqui você pode editar as informações cadastrais da sua empresa. Vamos conhecer os campos principais!',
      placement: 'center',
      disableBeacon: true,
    },
    {
      target: '[data-tour="page-title"]',
      content: 'Clique aqui no título para navegar e acessar outras seções do sistema.',
      placement: 'bottom',
      title: '↩️ Navegação pelo Título',
    },
    {
      target: '[data-tour="company-logo"]',
      content: 'Adicione ou atualize a logo da sua empresa. A imagem será exibida em diversos locais do sistema.',
      placement: 'bottom',
      title: '🖼️ Logo da Empresa',
    },
    {
      target: '[data-tour="company-info"]',
      content: 'Nesta seção você pode atualizar as informações básicas da empresa como nome fantasia e razão social.',
      placement: 'bottom',
      title: '📝 Informações Básicas',
    },
    {
      target: '[data-tour="legal-info"]',
      content: 'Aqui ficam os dados legais e de porte da empresa: CNPJ e quantidade de funcionários.',
      placement: 'bottom',
      title: '🏢 Dados Legais',
    },
    {
      target: '[data-tour="business-info"]',
      content: 'Configure informações sobre o negócio como receita anual e setor de atuação (indústria).',
      placement: 'bottom',
      title: '💼 Dados do Negócio',
    },
    {
      target: '[data-tour="locale-settings"]',
      content: 'Configure o idioma padrão e o fuso horário da empresa. Essas configurações afetam as comunicações do sistema e a exibição de datas.',
      placement: 'bottom',
      title: '🌐 Idioma e Fuso Horário',
    },
    {
      target: '[data-tour="domains"]',
      content: 'Adicione os domínios de e-mail da sua empresa. Digite o domínio e pressione Enter para adicionar.',
      placement: 'bottom',
      title: '🌐 Domínios',
    },
    {
      target: '[data-tour="description"]',
      content: 'Use este campo para adicionar uma descrição ou resumo sobre a empresa.',
      placement: 'top',
      title: '📄 Descrição',
    },
    {
      target: '[data-tour="save-button"]',
      content: 'Após fazer as alterações, clique aqui para salvar as mudanças no perfil da empresa.',
      placement: 'top',
      title: '💾 Salvar Alterações',
    },
    {
      target: 'body',
      content: 'Pronto! Agora você sabe como editar o perfil da empresa. Você pode reativar este tour clicando no botão de ajuda (?) quando precisar.',
      placement: 'center',
      title: '✨ Tour Concluído!',
    },
  ];

  return (
    <Container title={t('company:companyProfile.title')} breadcrumb={t('company:companyProfile.breadcrumb')} linkPage="#">
      {/* Helper Interativo */}
      {isSuccess && (
        <PageHelper
          steps={tourSteps}
          tourKey="company-profile-tour"
          autoStart={true}
          showButton={true}
        />
      )}

      <form onSubmit={handleSubmit(handleComplete)} className="w-100 mt-3">
        <Row alignItems="start" justifyContent="center">
          <Column xs={12}>
            <Card>
              {isLoading && !isError && (
                <>
                  <Row>
                    <Column xs={12}>
                      <Shimmer style={{ height: '48px' }}></Shimmer>
                    </Column>
                  </Row>
                  {[1, 2, 3, 4, 5].map((_, index) => (
                    <Row key={`shimer-${index}`}>
                      <Column xs={12}>
                        <Shimmer style={{ marginTop: '16px', height: '48px' }}></Shimmer>
                      </Column>
                    </Row>
                  ))}
                </>
              )}
              {isSuccess && (
                <>
                  <div data-tour="company-logo" style={{ marginBottom: '24px' }}>
                    <Row justifyContent="center">
                      <Column xs={12}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                          <Typography variant="h6" sx={{ mb: 1 }}>{t('company:companyProfile.logo.title')}</Typography>
                          <Avatar
                            src={logoPreview}
                            alt={t('company:companyProfile.logo.alt')}
                            sx={{
                              width: 150,
                              height: 150,
                              bgcolor: '#e0e0e0',
                              fontSize: '3rem',
                              color: '#757575'
                            }}
                          >
                            {!logoPreview && getValues('name')?.charAt(0)?.toUpperCase()}
                          </Avatar>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                              variant="outlined"
                              component="label"
                            >
                              {t('company:companyProfile.logo.choose')}
                              <input
                                type="file"
                                hidden
                                accept="image/*"
                                onChange={handleLogoUpload}
                              />
                            </Button>
                            {logoPreview && (
                              <Button
                                variant="outlined"
                                color="error"
                                onClick={handleRemoveLogo}
                              >
                                {t('company:companyProfile.logo.remove')}
                              </Button>
                            )}
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            {t('company:companyProfile.logo.hint')}
                          </Typography>
                        </Box>
                      </Column>
                    </Row>
                  </div>
                  <div data-tour="company-info">
                    <Row>
                      <Column xs={12} xl={6}>
                        <CustomTextField
                          {...register('name', { required: t('company:companyProfile.nameRequired') })}
                          label={t('company:companyProfile.name')}
                        autoFocus
                        error={errors?.name ? true : false}
                        sx={{ minHeight: '48px', margin: '0 auto 24px', width: '100%' }}
                        helperText={errors?.name ? errors.name.message : null}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Column>
                    <Column xs={12} xl={6}>
                      <CustomTextField
                        {...register('corporateName', { required: t('company:companyProfile.corporateNameRequired') })}
                        label={t('company:companyProfile.corporateName')}
                        autoFocus
                        error={errors?.corporateName ? true : false}
                        sx={{ minHeight: '48px', margin: '0 auto 24px', width: '100%' }}
                        helperText={errors?.corporateName ? errors.corporateName.message : null}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Column>
                  </Row>
                  </div>
                  <div data-tour="legal-info">
                  <Row>
                    <Column xs={12} xl={4}>
                      <CustomTextField
                        {...register('cnpj', { required: t('company:companyProfile.cnpjRequired') })}
                        label={t('company:companyProfile.cnpj')}
                        disabled
                        error={errors?.cnpj ? true : false}
                        sx={{ minHeight: '48px', margin: '0 auto 24px', width: '100%' }}
                        helperText={errors?.cnpj ? errors.cnpj.message : t('company:companyProfile.cnpjReadonly')}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Column>
                    <Column xs={12} xl={4}>
                      <CustomTextField
                        value={countryLabel}
                        label={t('company:companyProfile.country')}
                        disabled
                        sx={{ minHeight: '48px', margin: '0 auto 24px', width: '100%' }}
                        helperText={t('company:companyProfile.countryReadonly')}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Column>
                    <Column xs={12} xl={4}>
                      <CustomTextField
                        {...register('size')}
                        label={t('company:companyProfile.size')}
                        sx={{ minHeight: '48px', margin: '0 auto 24px', width: '100%' }}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Column>
                  </Row>
                  </div>
                  <div data-tour="business-info">
                  <Row>
                    <Column xs={12} xl={6}>
                      <CustomTextField
                        {...register('income')}
                        label={t('company:companyProfile.income')}
                        sx={{ minHeight: '48px', margin: '0 auto 24px', width: '100%' }}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Column>
                    <Column xs={12} xl={6}>
                      <CustomTextField
                        {...register('industry')}
                        label={t('company:companyProfile.industry')}
                        sx={{ minHeight: '48px', margin: '0 auto 24px', width: '100%' }}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Column>
                  </Row>
                  </div>
                  <div data-tour="locale-settings">
                  <Row>
                    <Column xs={12} xl={6}>
                      <Controller
                        control={control}
                        name="defaultLanguage"
                        rules={{ required: t('company:companyProfile.languageRequired') }}
                        render={({ field: { onChange, value } }) => (
                          <FormControl fullWidth sx={{ marginBottom: '24px' }} error={!!errors?.defaultLanguage}>
                            <InputLabel shrink>{t('company:companyProfile.language')}</InputLabel>
                            <Select
                              value={value || ''}
                              onChange={onChange}
                              notched
                              label={t('company:companyProfile.language')}
                              displayEmpty
                            >
                              <MenuItem value="pt">{t('company:companyProfile.languagePt')}</MenuItem>
                              <MenuItem value="en">{t('company:companyProfile.languageEn')}</MenuItem>
                              <MenuItem value="es">{t('company:companyProfile.languageEs')}</MenuItem>
                            </Select>
                            {errors?.defaultLanguage && <FormHelperText>{errors.defaultLanguage.message}</FormHelperText>}
                          </FormControl>
                        )}
                      />
                    </Column>
                    <Column xs={12} xl={6}>
                      <Controller
                        control={control}
                        name="timezone"
                        render={({ field: { onChange, value } }) => (
                          <FormControl fullWidth sx={{ marginBottom: '24px' }}>
                            <InputLabel shrink>{t('company:companyProfile.timezone')}</InputLabel>
                            <Select
                              value={value || ''}
                              onChange={onChange}
                              notched
                              label={t('company:companyProfile.timezone')}
                              displayEmpty
                            >
                              <MenuItem value="">{t('company:companyProfile.timezonePlaceholder')}</MenuItem>
                              {LATAM_TIMEZONES.map(tz => (
                                <MenuItem key={tz.value} value={tz.value}>
                                  {tz.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                      />
                    </Column>
                  </Row>
                  </div>
                  <div data-tour="domains">
                  <Row justifyContent="center">
                    <Column xs={12} className="d-flex">
                      <FormControl fullWidth sx={{ margin: '0 auto 24px' }}>
                        <Controller
                          control={control}
                          name="domains"
                          render={({ field: { onChange, value } }) => (
                            <MuiChipsInput
                              label={t('company:companyProfile.domains')}
                              value={value}
                              onChange={onChange}
                              InputLabelProps={{ shrink: true }}
                              placeholder={t('company:companyProfile.domainsPlaceholder')}
                              renderChip={(Component, props) => {
                                return (
                                  <Component
                                    {...props}
                                    variant="outlined"
                                    color="primary"
                                    sx={{
                                      width: 'fit-content',
                                    }}
                                  />
                                );
                              }}
                              sx={{
                                '& .MuiButtonBase-root.MuiChip-root.MuiChip-filled': {
                                  width: 'fit-content',
                                },
                                '& .MuiButtonBase-root.MuiIconButton-root': {
                                  width: 'fit-content',
                                  marginTop: '0px',
                                },
                              }}
                            />
                          )}
                        ></Controller>
                      </FormControl>
                    </Column>
                  </Row>
                  </div>
                  <div data-tour="description">
                  <Row>
                    <Column xs={12}>
                      <TextField
                        {...register('description')}
                        label={t('company:companyProfile.description')}
                        multiline
                        rows={3}
                        sx={{ margin: '0 auto 4px', width: '100%' }}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Column>
                  </Row>
                  </div>
                  <div data-tour="save-button">
                  <Row>
                    <Column xs={12} justifyContent="end" flex className="mt-3">
                      <ButtonForm label={t('company:companyProfile.save')} type="submit" />
                    </Column>
                  </Row>
                  </div>
                </>
              )}
            </Card>
          </Column>
        </Row>
      </form>
    </Container>
  );
};
